<template>
  <div class="ret-gallery">
    <p v-if="!photos.length" class="ret-gallery__empty">{{ $t("returns.detail.noPhotos") }}</p>

    <figure v-for="p in photos" :key="p.id" class="ret-gallery__item">
      <img v-if="urls[p.id]" :src="urls[p.id]" alt="" @click="openViewer(p.id)" />
      <div v-else-if="p.isNotAvailable" class="ret-gallery__na">
        <md-icon>block</md-icon>
      </div>
      <div v-else class="ret-gallery__loading">
        <md-progress-spinner md-mode="indeterminate" :md-diameter="26"></md-progress-spinner>
      </div>
      <figcaption>
        {{ typeLabel(p.photoTypeCode) }}
        <span v-if="p.isNotAvailable" class="ret-gallery__natag">{{ $t("returns.photos.notAvailable") }}</span>
      </figcaption>
    </figure>

    <!-- Grossansicht mit X und Blaettern durch alle Fotos des Vorgangs (Kundenwunsch 18.08., P17.1) -->
    <div v-if="viewerIndex !== null" class="ret-viewer" @click.self="closeViewer">
      <button class="ret-viewer__close" :aria-label="$t('returns.detail.close')" @click="closeViewer">
        <md-icon>close</md-icon>
      </button>
      <button v-if="viewablePhotos.length > 1" class="ret-viewer__nav ret-viewer__nav--prev" @click.stop="stepViewer(-1)">
        <md-icon>chevron_left</md-icon>
      </button>
      <img :src="urls[viewablePhotos[viewerIndex].id]" alt="" />
      <button v-if="viewablePhotos.length > 1" class="ret-viewer__nav ret-viewer__nav--next" @click.stop="stepViewer(1)">
        <md-icon>chevron_right</md-icon>
      </button>
      <div class="ret-viewer__caption">{{ typeLabel(viewablePhotos[viewerIndex].photoTypeCode) }} ({{ viewerIndex + 1 }}/{{ viewablePhotos.length }})</div>
    </div>
  </div>
</template>

<script>
import ReturnsService from "@/services/returns.service";

// Fotos werden wie die Lieferplan-Dokumente per axios mit Token als Blob geladen
// (der Endpunkt ist authentifiziert, ein blosses <img src> wuerde 401 bekommen).
export default {
  name: "ReturnPhotoGallery",
  props: {
    returnId: { type: Number, required: true },
    photos: { type: Array, default: () => [] },
  },
  data() {
    return { urls: {}, viewerIndex: null };
  },
  computed: {
    // Nur echte Fotos sind blaetterbar - "nicht vorhanden"-Slots werden uebersprungen
    viewablePhotos() {
      return this.photos.filter((p) => !p.isNotAvailable && this.urls[p.id]);
    },
  },
  watch: {
    photos: {
      immediate: true,
      handler() {
        this.loadThumbnails();
      },
    },
  },
  beforeDestroy() {
    Object.values(this.urls).forEach((u) => URL.revokeObjectURL(u));
  },
  methods: {
    openViewer(photoId) {
      const idx = this.viewablePhotos.findIndex((p) => p.id === photoId);
      if (idx >= 0) this.viewerIndex = idx;
    },
    closeViewer() {
      this.viewerIndex = null;
    },
    stepViewer(delta) {
      const n = this.viewablePhotos.length;
      if (!n) return;
      this.viewerIndex = (this.viewerIndex + delta + n) % n;
    },
    typeLabel(code) {
      const key = "returns.photoTypes." + code;
      return this.$te(key) ? this.$t(key) : code;
    },
    async loadThumbnails() {
      for (const p of this.photos) {
        if (p.isNotAvailable || this.urls[p.id]) continue;
        const url = await ReturnsService.photoContentUrl(this.returnId, p.id).catch(() => null);
        if (url) this.$set(this.urls, p.id, url);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.ret-gallery {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;

  &__empty {
    color: #9e9e9e;
    font-size: 13px;
  }

  &__item {
    margin: 0;
    width: 140px;

    img {
      width: 140px;
      height: 110px;
      object-fit: cover;
      border-radius: 6px;
      cursor: zoom-in;
      display: block;
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.16);
    }

    figcaption {
      font-size: 12px;
      color: #616161;
      padding-top: 4px;
      text-align: center;
    }
  }

  &__na,
  &__loading {
    width: 140px;
    height: 110px;
    border-radius: 6px;
    background: #fafafa;
    border: 1px dashed #e0e0e0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #bdbdbd;
  }

  &__natag {
    display: block;
    font-size: 11px;
    color: #9e9e9e;
  }
}

.ret-viewer {
  position: fixed;
  inset: 0;
  z-index: 10002;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    max-width: calc(100vw - 140px);
    max-height: calc(100vh - 80px);
    object-fit: contain;
    border-radius: 4px;
  }

  &__close,
  &__nav {
    position: absolute;
    border: none;
    border-radius: 50%;
    width: 44px;
    height: 44px;
    background: rgba(255, 255, 255, 0.92);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__close {
    top: 14px;
    right: 14px;
  }

  &__nav {
    top: 50%;
    transform: translateY(-50%);

    &--prev {
      left: 14px;
    }
    &--next {
      right: 14px;
    }
  }

  &__caption {
    position: absolute;
    bottom: 12px;
    left: 0;
    right: 0;
    text-align: center;
    color: #fff;
    font-size: 13px;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.7);
  }
}
</style>
